import type { ConnectOptions } from '..';
import { BaseDevice } from '../base';

import { convertReadingToObject } from './convertReatingToObject';

import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { toast } from 'svelte-sonner';
import type { Nullable } from '$lib/types/RoastLog';

/*
Web Bluetooth Docs:
https://developers.google.com/web/updates/2015/07/interact-with-ble-devices-on-the-web
*/
const serviceUuid = '2bbe0001-4086-9e55-d358-7558e6dd0b93';
const characteristicUuid = '2bbe0003-4086-9e55-d358-7558e6dd0b93';
const bluetoothOptions = { filters: [{ services: [serviceUuid] }] };

export class RLogBTLEDevice extends BaseDevice {
  type: DeviceType = DeviceType.RLBTLE;
  private rlogBTLEDevice: Nullable<BluetoothDevice> = null;

  protected async onConnect(): Promise<boolean> {
    return await this.connectWebBluetooth();
  }

  protected onDisconnect(): void {
    if (this.rlogBTLEDevice !== null) {
      this.rlogBTLEDevice.gatt.disconnect();
    }
  }

  protected async updateReadings(): Promise<void> {
    // No need to do anything here as the readings are updated via callbacks
    // from the Bluetooth library
  }

  private async connectWebBluetooth(): Promise<boolean> {
    try {
      logger.info('Databridge btle connect init');

      this.rlogBTLEDevice = await navigator.bluetooth.requestDevice(bluetoothOptions);
      this.rlogBTLEDevice.addEventListener('gattserverdisconnected', this.disconnectListener);

      const server = await this.rlogBTLEDevice.gatt.connect();
      const service = await server.getPrimaryService(serviceUuid);

      const characteristic = await service.getCharacteristic(characteristicUuid);
      await characteristic.startNotifications();

      characteristic.addEventListener('characteristicvaluechanged', this.convertDataBridgeReading);
      logger.info('Databridge btle connect success');

      return true;
    } catch (e) {
      logger.info('Databridge btle connection error', { error: e });
      toast.error(`Could not connect to Bluetooth Data Bridge. ${e}`);
      return false;
    }
  }

  private convertDataBridgeReading = (event: { target: { value: DataView } }): void => {
    const dataview = event.target.value;
    const readings = Array.from(new Uint8Array(dataview.buffer));

    // When the first two bits are [1, 2], it's USB. We can throw those away.
    if (readings[0] === 1 && readings[1] === 2) {
      readings.splice(0, 2);
    }

    if (!this.tcSettings) return;

    const data = convertReadingToObject(readings, [
      this.tcSettings[0].type,
      this.tcSettings[1].type,
      this.tcSettings[2].type,
      this.tcSettings[3].type
    ]);

    this.localReadings = [
      data.tc1,
      this.tcSettings[1].active ? data.tc2 : null,
      this.tcSettings[2].active ? data.tc3 : null,
      this.tcSettings[3].active ? data.tc4 : null
    ];
  };

  private disconnectListener = (): void => {
    this.stopTimer();
  };
}

const vendorId = 0x2544;
const productId = 0x0005;
const usbOptions = { filters: [{ vendorId, productId }] };

export class RLogUSBDevice extends BaseDevice {
  type: DeviceType = DeviceType.RLUSB;
  private rlogUSBDevice: Nullable<USBDevice> = null;
  private closing = false;

  protected async onConnect(): Promise<boolean> {
    return await this.connectWebUSB();
  }

  protected async onDisconnect(): Promise<void> {
    if (this.rlogUSBDevice !== null) {
      if ('close' in this.rlogUSBDevice && this.rlogUSBDevice.opened && !this.closing) {
        this.closing = true; // Set the flag before closing
        try {
          await (this.rlogUSBDevice as any).close(); // Await the close operation
        } catch (error) {
          logger.warn(error as any);
        } finally {
          this.closing = false; // Reset the flag
        }
      } else {
        logger.info('USB close method not available or already closing');
      }
    }
  }

  protected async updateReadings(): Promise<void> {
    const value = await this.readUsbData();
    if (value === null) return;

    const e = { target: { value } };
    this.convertDataBridgeReading(e);
  }

  private async connectWebUSB(): Promise<boolean> {
    try {
      this.rlogUSBDevice = await navigator.usb.requestDevice(usbOptions);
      navigator.usb.addEventListener('disconnect', this.disconnectListener);

      // Open connection to the device
      await this.rlogUSBDevice.open();
      // Select the first configuration if none is selected
      if (this.rlogUSBDevice.configuration === null) {
        await this.rlogUSBDevice.selectConfiguration(1);
      }

      // Claim the first interface
      await this.rlogUSBDevice.claimInterface(0);

      return true;
    } catch (e) {
      logger.info('Databridge usb connection error', { error: e });
      toast.error(`Could not connect to USB Data Bridge. ${e}`);
      return false;
    }
  }

  private convertDataBridgeReading = (event: { target: { value: DataView } }): void => {
    const dataview = event.target.value;
    const readings = Array.from(new Uint8Array(dataview.buffer));

    // When the first two bits are [1, 2], it's USB. We can throw those away.
    if (readings[0] === 1 && readings[1] === 2) {
      readings.splice(0, 2);
    }

    if (!this.tcSettings) return;

    const data = convertReadingToObject(readings, [
      this.tcSettings[0].type,
      this.tcSettings[1].type,
      this.tcSettings[2].type,
      this.tcSettings[3].type
    ]);

    this.localReadings = [
      data.tc1,
      this.tcSettings[1].active ? data.tc2 : null,
      this.tcSettings[2].active ? data.tc3 : null,
      this.tcSettings[3].active ? data.tc4 : null
    ];
  };

  private disconnectListener = (): void => {
    this.stopTimer();
  };

  private async readUsbData(): Promise<DataView | null> {
    if (!this.rlogUSBDevice) {
      return null;
    }

    try {
      const result = await this.rlogUSBDevice.transferIn(1, 64);

      if (result.data && result.data.byteLength === 21) {
        return result.data as DataView;
      }

      if (result.status === 'stall') {
        await this.rlogUSBDevice.clearHalt(1);
      }
    } catch (e) {
      logger.error('Data bridge USB disconnect', { error: e });
    }
    return null;
  }
}
